import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Badge, Input, Select, TextArea, Modal, Table } from '../components/Common';

export default function Clients() {
  const { apiFetch, hasGroup } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const isSuperAdmin = hasGroup('Super Admins');

  // Licensing state
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [licenseClient, setLicenseClient] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [activeUsers, setActiveUsers] = useState(0);
  const [showLicenseForm, setShowLicenseForm] = useState(false);
  const [licForm, setLicForm] = useState({});
  const [editingLicense, setEditingLicense] = useState(null);

  // Vendor state
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [vendorClient, setVendorClient] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [vendorForm, setVendorForm] = useState('');
  const [editingVendor, setEditingVendor] = useState(null);

  const openVendors = async (client) => {
    setVendorClient(client);
    try {
      const res = await apiFetch(`/client-vendors/${client.id}?includeInactive=true`);
      if (res.ok) setVendors(await res.json());
    } catch {}
    setShowVendorModal(true);
  };

  const saveVendor = async () => {
    if (!vendorForm.trim()) return;
    const method = editingVendor ? 'PUT' : 'POST';
    const url = editingVendor ? `/client-vendors/${vendorClient.id}/${editingVendor}` : `/client-vendors/${vendorClient.id}`;
    const res = await apiFetch(url, { method, body: JSON.stringify({ name: vendorForm.trim() }) });
    if (!res.ok) { const err = await res.json(); alert(err.error); return; }
    setVendorForm(''); setEditingVendor(null);
    openVendors(vendorClient);
  };

  const toggleVendorActive = async (vendor) => {
    const res = await apiFetch(`/client-vendors/${vendorClient.id}/${vendor.id}`, {
      method: 'PUT', body: JSON.stringify({ isActive: !vendor.is_active })
    });
    if (!res.ok) { const err = await res.json(); alert(err.error); return; }
    openVendors(vendorClient);
  };

  const deleteVendor = async (vendor) => {
    if (!window.confirm(`Delete vendor "${vendor.name}"?`)) return;
    const res = await apiFetch(`/client-vendors/${vendorClient.id}/${vendor.id}`, { method: 'DELETE' });
    if (!res.ok) { const err = await res.json(); alert(err.error); return; }
    openVendors(vendorClient);
  };

  const load = useCallback(async () => {
    const res = await apiFetch('/clients');
    setClients(await res.json());
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const body = { name: form.name, contactName: form.contactName, contactEmail: form.contactEmail, phone: form.phone, address: form.address, aiEnabled: !!form.aiEnabled };
    if (editing) {
      await apiFetch(`/clients/${editing.id}`, { method: 'PUT', body: JSON.stringify(body) });
    } else {
      await apiFetch('/clients', { method: 'POST', body: JSON.stringify(body) });
    }
    setShowModal(false); setForm({}); setEditing(null); load();
  };

  const toggleActive = async (client) => {
    await apiFetch(`/clients/${client.id}`, {
      method: 'PUT',
      body: JSON.stringify({ isActive: !client.is_active })
    });
    load();
  };

  // License functions
  const openLicenses = async (client) => {
    setLicenseClient(client);
    const res = await apiFetch(`/licenses/client/${client.id}`);
    if (res.ok) {
      const data = await res.json();
      setLicenses(data.licenses || []);
      setActiveUsers(data.activeUsers || 0);
    }
    setShowLicenseModal(true);
  };

  const openNewLicense = () => {
    setEditingLicense(null);
    setLicForm({ seatCount: '', activeDate: new Date().toISOString().split('T')[0] });
    setShowLicenseForm(true);
  };

  const openEditLicense = (lic) => {
    setEditingLicense(lic);
    setLicForm({
      seatCount: lic.seat_count != null ? String(lic.seat_count) : '',
      activeDate: lic.active_date?.split('T')[0] || '',
      expiryDate: lic.expiry_date?.split('T')[0] || '',
      status: lic.status,
      notes: lic.notes || ''
    });
    setShowLicenseForm(true);
  };

  const saveLicense = async () => {
    const body = {
      clientId: licenseClient.id,
      seatCount: licForm.seatCount ? parseInt(licForm.seatCount) : null,
      activeDate: licForm.activeDate,
      expiryDate: licForm.expiryDate || null,
      status: licForm.status || 'Active',
      notes: licForm.notes || null
    };
    if (editingLicense) {
      await apiFetch(`/licenses/${editingLicense.id}`, { method: 'PUT', body: JSON.stringify(body) });
    } else {
      const res = await apiFetch('/licenses', { method: 'POST', body: JSON.stringify(body) });
      if (!res.ok) { const err = await res.json(); alert(err.error); return; }
    }
    setShowLicenseForm(false);
    setLicForm({});
    setEditingLicense(null);
    openLicenses(licenseClient);
  };

  const deleteLicense = async (id) => {
    if (!window.confirm('Delete this license?')) return;
    await apiFetch(`/licenses/${id}`, { method: 'DELETE' });
    openLicenses(licenseClient);
  };

  const isExpired = (lic) => lic.expiry_date && new Date(lic.expiry_date) < new Date();
  const activeLicense = (clientId) => {
    // This is a simple check for display — not authoritative
    return null;
  };

  const filteredClients = showInactive ? clients : clients.filter(c => c.is_active !== false);

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'contact_name', label: 'Contact' },
    { key: 'contact_email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'license_expiry', label: 'License Expiry', render: (v, row) => {
      if (!v) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
      const d = new Date(v);
      const now = new Date();
      const days = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
      const color = days <= 0 ? '#ef4444' : days <= 30 ? '#f59e0b' : '#22c55e';
      return <span style={{ fontWeight: 600, color }}>{d.toLocaleDateString()}{days <= 30 && ` (${days <= 0 ? 'Expired' : days + 'd'})`}</span>;
    }},
    { key: 'ai_enabled', label: 'AI', render: v => v === true
      ? <span title="AI Pentest Agent enabled" style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)' }}>AI</span>
      : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span> },
    { key: 'is_active', label: 'Status', render: v => v === false ? '❌ Inactive' : '✅ Active' },
    { key: 'id', label: 'Actions', render: (v, row) => (
      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
          onClick={(e) => { e.stopPropagation(); setEditing(row); setForm({ name: row.name, contactName: row.contact_name, contactEmail: row.contact_email, phone: row.phone, address: row.address, aiEnabled: row.ai_enabled === true }); setShowModal(true); }}>
          Edit
        </Button>
        <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12, background: '#8b5cf6', color: '#fff', border: 'none' }}
          onClick={(e) => { e.stopPropagation(); openLicenses(row); }}>
          Licenses
        </Button>
        <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12, background: '#059669', color: '#fff', border: 'none' }}
          onClick={(e) => { e.stopPropagation(); openVendors(row); }}>
          Vendors
        </Button>
        {hasGroup('Client Admins') && (
          <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12, background: '#0052CC', color: '#fff', border: 'none' }}
            onClick={(e) => { e.stopPropagation(); navigate(`/integrations/${row.id}`); }}>
            Integrations
          </Button>
        )}
        {isSuperAdmin && (
          <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
            onClick={(e) => { e.stopPropagation(); toggleActive(row); }}>
            {row.is_active === false ? 'Activate' : 'Deactivate'}
          </Button>
        )}
      </div>
    )}
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>Clients</h1>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Button variant={showInactive ? 'primary' : 'secondary'} style={{ padding: '8px 16px', fontSize: 13 }}
            onClick={() => setShowInactive(!showInactive)}>
            {showInactive ? 'Showing All' : 'Show Inactive'}
          </Button>
          <Button onClick={() => { setEditing(null); setForm({}); setShowModal(true); }}>+ New Client</Button>
        </div>
      </div>
      <Table columns={columns} data={filteredClients} />

      {/* Client Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit Client' : 'New Client'}>
        <Input label="Name *" value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        <Input label="Contact Name" value={form.contactName || ''} onChange={e => setForm(f => ({ ...f, contactName: e.target.value }))} />
        <Input label="Contact Email" value={form.contactEmail || ''} onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))} />
        <Input label="Phone" value={form.phone || ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
        <Input label="Address" value={form.address || ''} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '4px 0 16px', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!form.aiEnabled} style={{ marginTop: 3 }}
            onChange={e => setForm(f => ({ ...f, aiEnabled: e.target.checked }))} />
          <span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>AI Enabled</span>
            <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)' }}>
              Allows this client's users to be granted the AI Pentest group and run the
              AI Pentest Agent. Turning it off withdraws access immediately, without
              changing anyone's groups.
            </span>
          </span>
        </label>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={save} disabled={!form.name}>Save</Button>
        </div>
      </Modal>

      {/* License Management Modal */}
      <Modal isOpen={showLicenseModal} onClose={() => { setShowLicenseModal(false); setShowLicenseForm(false); }}
        title={licenseClient ? `Licensing — ${licenseClient.name}` : 'Licensing'} width={700} noOverlayClose>
        {licenseClient && !showLicenseForm && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                Active Users: <strong style={{ color: 'var(--text)' }}>{activeUsers}</strong>
              </div>
              <Button onClick={openNewLicense}>+ New License</Button>
            </div>

            {licenses.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {licenses.map(lic => (
                  <Card key={lic.id} style={{ padding: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <code style={{ fontSize: 14, fontWeight: 600, color: 'var(--primary)', background: 'var(--bg-input)', padding: '2px 8px', borderRadius: 4 }}>
                            {lic.license_key}
                          </code>
                          <Badge style={{
                            background: lic.status === 'Active' && !isExpired(lic) ? '#22c55e' :
                              lic.status === 'Suspended' ? '#f59e0b' : '#ef4444',
                            color: '#fff'
                          }}>
                            {isExpired(lic) ? 'Expired' : lic.status}
                          </Badge>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
                          <div><strong>Seats:</strong> {lic.seat_count != null ? `${activeUsers} / ${lic.seat_count}` : 'Unrestricted'}</div>
                          <div><strong>Active Date:</strong> {lic.active_date ? new Date(lic.active_date).toLocaleDateString() : '—'}</div>
                          <div><strong>Expiry Date:</strong> {lic.expiry_date ? new Date(lic.expiry_date).toLocaleDateString() : 'Never'}</div>
                          {lic.notes && <div style={{ gridColumn: '1 / -1' }}><strong>Notes:</strong> {lic.notes}</div>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <Button variant="secondary" style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => openEditLicense(lic)}>Edit</Button>
                        <Button variant="danger" style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => deleteLicense(lic.id)}>Delete</Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>
                No licenses configured for this client.
              </Card>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <Button variant="secondary" onClick={() => { setShowLicenseModal(false); setShowLicenseForm(false); }}>Close</Button>
            </div>
          </div>
        )}

        {showLicenseForm && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>
                  Seat Count
                </label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Input type="number" min="1" value={licForm.seatCount || ''} placeholder="Unrestricted"
                    onChange={e => setLicForm(f => ({ ...f, seatCount: e.target.value }))} style={{ marginBottom: 0, flex: 1 }} />
                  <Button variant="ghost" style={{ padding: '6px 10px', fontSize: 11 }}
                    onClick={() => setLicForm(f => ({ ...f, seatCount: '' }))}>Unrestricted</Button>
                </div>
              </div>
              <Select label="Status" value={licForm.status || 'Active'}
                onChange={e => setLicForm(f => ({ ...f, status: e.target.value }))}
                options={[
                  { value: 'Active', label: 'Active' },
                  { value: 'Suspended', label: 'Suspended' },
                  { value: 'Cancelled', label: 'Cancelled' }
                ]} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Input label="Active Date *" type="date" value={licForm.activeDate || ''}
                onChange={e => setLicForm(f => ({ ...f, activeDate: e.target.value }))} />
              <Input label="Expiry Date" type="date" value={licForm.expiryDate || ''}
                onChange={e => setLicForm(f => ({ ...f, expiryDate: e.target.value }))} />
            </div>
            <TextArea label="Notes" value={licForm.notes || ''} placeholder="Internal notes about this license..."
              onChange={e => setLicForm(f => ({ ...f, notes: e.target.value }))} />
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
              <Button variant="secondary" onClick={() => setShowLicenseForm(false)}>Back</Button>
              <Button onClick={saveLicense} disabled={!licForm.activeDate}>
                {editingLicense ? 'Update License' : 'Create License'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Vendor Modal */}
      <Modal isOpen={showVendorModal} onClose={() => { setShowVendorModal(false); setEditingVendor(null); setVendorForm(''); }}
        title={`Vendors — ${vendorClient?.name || ''}`} width={500}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input value={vendorForm} onChange={e => setVendorForm(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') saveVendor(); }}
            placeholder={editingVendor ? 'Edit vendor name...' : 'New vendor name...'}
            style={{ flex: 1, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, background: 'var(--bg-input)', color: 'var(--text)', outline: 'none', fontFamily: 'inherit' }} />
          <Button onClick={saveVendor} disabled={!vendorForm.trim()} style={{ fontSize: 12, padding: '8px 14px' }}>
            {editingVendor ? 'Update' : '+ Add'}
          </Button>
          {editingVendor && <Button variant="secondary" onClick={() => { setEditingVendor(null); setVendorForm(''); }} style={{ fontSize: 12, padding: '8px 12px' }}>Cancel</Button>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {vendors.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 13, padding: '12px 0' }}>No vendors yet</p>
          ) : vendors.map(v => (
            <div key={v.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8,
              background: 'var(--bg-input)', opacity: v.is_active ? 1 : 0.5
            }}>
              {v.is_msp_default && (
                <span style={{ fontSize: 10, fontWeight: 600, color: '#4f8fff', background: 'rgba(79,143,255,0.1)', padding: '2px 6px', borderRadius: 6 }}>MSP</span>
              )}
              <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{v.name}</span>
              {!v.is_active && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Inactive</span>}
              {!v.is_msp_default && (
                <>
                  <button onClick={() => { setEditingVendor(v.id); setVendorForm(v.name); }}
                    style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, padding: '2px 6px' }}>Edit</button>
                  <button onClick={() => toggleVendorActive(v)}
                    style={{ background: 'none', border: 'none', color: v.is_active ? '#f59e42' : '#34d399', cursor: 'pointer', fontSize: 12, padding: '2px 6px' }}>
                    {v.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  {v.is_active === false && (
                    <button onClick={() => deleteVendor(v)}
                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12, padding: '2px 6px' }}>Delete</button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
